import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
    Box,
    Container,
    FormLabel,
    Heading,
    InputGroup,
    Link,
    Text,
} from '@chakra-ui/react';
import { ChakraStylesConfig, Select } from 'chakra-react-select';
import { FC } from 'react';
import {
    LoaderFunctionArgs,
    Link as ReactRouterLink,
    useLoaderData,
    useNavigate,
} from 'react-router-dom';
import { fetchAppointments } from '../../api/repository/appointment';
import { fetchStock } from '../../api/repository/stock';
import { fetchUserMe } from '../../api/repository/user';
import { PagePath, createAppointmentNewRoute } from '../../constants/routes';
import { convertAppointmentFromApi } from '../../helpers/appointment';
import {
    convertStockItemFromApi,
    findAppointment,
    findStock,
} from '../../helpers/stock';
import { generateSelectOption } from '../../helpers/stockSelect';
import { convertUserFromApi } from '../../helpers/user';
import { AppointmentInterface } from '../../types/appointment';
import {
    StockItemInterface,
    StockSelectOptionInterface,
} from '../../types/stock';
import { UserInterface } from '../../types/user';
import { AppointmentDetail } from './AppointmentDetail';

export const loader = async ({ params }: LoaderFunctionArgs) => {
    const { id: stockNumber } = params;
    const stock = await fetchStock();
    const appointments = await fetchAppointments();
    const user = await fetchUserMe();

    const formattedAppointments = appointments?.map((item) =>
        convertAppointmentFromApi(item)
    );

    const formattedStock = stock?.map((item) => convertStockItemFromApi(item, formattedAppointments));

    const formattedUser = convertUserFromApi(user);

    const currentStock = stockNumber
        ? findStock(formattedStock, stockNumber)
        : null;

    if (!currentStock) {
        return {
            stock: formattedStock,
            user: formattedUser,
        };
    }

    const existingAppointment =
        findAppointment(formattedAppointments, currentStock.stockNumber) ??
        null;

    const selectedStockOption = generateSelectOption(currentStock);

    return {
        stock: formattedStock,
        currentStock,
        selectedStockOption,
        existingAppointment,
        user: formattedUser,
    };
};

export const Appointment: FC = () => {
    const {
        stock,
        currentStock,
        selectedStockOption,
        existingAppointment,
        user,
    } = useLoaderData() as {
        stock: StockItemInterface[];
        user: UserInterface;
        currentStock: StockItemInterface | null;
        selectedStockOption: StockSelectOptionInterface | null;
        existingAppointment: AppointmentInterface | null;
    };
    const navigate = useNavigate();

    const selectOptions = stock.map((stockItem) =>
        generateSelectOption(stockItem)
    );

    const onSelectStockChange = (val: unknown) => {
        if (val) {
            return navigate(
                createAppointmentNewRoute(
                    (val as StockSelectOptionInterface).value.stockNumber
                )
            );
        }

        return navigate(PagePath.Appointment);
    };

    const selectChakraStyles: ChakraStylesConfig = {
        container: (provided) => ({
            ...provided,
            width: '240px',
        }),
    };

    return (
        <Box px={8} py={8}>
            <Link
                as={ReactRouterLink}
                to={PagePath.StockOverview}
                mb={2}
                display={'inline-block'}
            >
                <ChevronLeftIcon />
                Terug naar overzicht
            </Link>
            <Container maxW={'4xl'} px={0}>
                <Heading color={'secondary'}>Afspraak maken</Heading>
                <Text mb={6}>
                    Maak eenvoudig online een afspraak voor onderhoud of
                    reparatie van een van uw voertuigen.
                </Text>

                <InputGroup
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    mb={4}
                >
                    <FormLabel m={0}>Voertuig</FormLabel>
                    <Select
                        placeholder="Zoeken..."
                        options={selectOptions}
                        defaultValue={selectedStockOption}
                        isClearable={true}
                        chakraStyles={selectChakraStyles}
                        onChange={onSelectStockChange}
                        menuPortalTarget={document.querySelector('body')}
                    />
                </InputGroup>

                <AppointmentDetail
                    currentStock={currentStock}
                    userLocations={user.locations}
                    existingAppointment={existingAppointment}
                />
            </Container>
        </Box>
    );
};
